import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { AbsenceService } from '@app/reducers/orm/absence/absence.service';
import { CorrectionService } from '@app/reducers/orm/correction/correction.service';
import { Balance, BalanceExpiry } from '@app/reducers/orm/employee/employee.model';
import { getEmployeeEntities } from '@app/reducers/orm/employee/employee.selectors';
import { EmployeeService } from '@app/reducers/orm/employee/employee.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { TooltipDirective } from '@sb/tooltip';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent } from '@sb/ui/icon';
import { parseDate, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { subDays } from 'date-fns';
import { combineLatest, merge, take } from 'rxjs';

import type { EmployeeOverviewData } from '../employee-absence-overview.model';
import { TimeOffBalanceComponent } from './time-off-balance/time-off-balance.component';

interface BalanceExpiriesMap {
  [key: string]: BalanceExpiry[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'time-off-balances',
  templateUrl: './time-off-balances.component.html',
  imports: [
    TranslatePipe,
    LocaleDatePipe,
    TimeOffBalanceComponent,
    IconComponent,
    TooltipDirective,
    LoadingStateComponent,
    EmptyStateComponent,
  ],
})
export class TimeOffBalancesComponent implements OnChanges {
  private employeeService = inject(EmployeeService);
  private correctionService = inject(CorrectionService);
  private absenceService = inject(AbsenceService);
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  year = input<string>();

  @Input()
  employee: EmployeeOverviewData;

  startDate = computed<string>(() => `${this.year()}-01-01`);
  endDate = computed<string>(() =>
    this.endContractDate() && new Date(this.endContractDate()).getFullYear() === Number(this.year())
      ? this.endContractDate()
      : `${this.year()}-12-31`,
  );
  endContractDate = signal<string | undefined>(undefined);

  balances: Balance[];
  balanceExpiriesMap: BalanceExpiriesMap = {};
  balanceStatusMap: Record<string, boolean> = {};
  loading = false;

  constructor() {
    void merge(this.correctionService.listenToChanges(), this.absenceService.listenToChanges())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getBalances();
        this.getBalanceExpiries();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['employee'] || changes['year']) {
      this.getBalances();
    }

    if (changes['employee']) {
      if (this.employee.endDate) {
        this.endContractDate.set(toISODateString(subDays(parseDate(this.employee.endDate), 1)));
      } else {
        this.endContractDate.set(undefined);
      }

      this.getBalanceExpiries();
    }
  }

  onBalanceToggle(balance: Balance) {
    this.balanceStatusMap[balance.timeOffBalanceId] = !this.balanceStatusMap[balance.timeOffBalanceId];
    this.updateBalanceStatusMap(false, balance.timeOffBalanceId);
  }

  private updateBalanceStatusMap(override?: boolean, skipOverrideFor?: string) {
    if (this.balances?.length) {
      this.balanceStatusMap = this.balances.reduce<Record<string, boolean>>((map, balance) => {
        map[balance.timeOffBalanceId] =
          override !== undefined && skipOverrideFor !== balance.timeOffBalanceId
            ? override
            : this.balanceStatusMap[balance.timeOffBalanceId] || false;
        return map;
      }, {});
    }
  }

  private getBalanceExpiries() {
    void this.employeeService
      .getBalanceExpiries(this.employee.id)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          const balanceMap: BalanceExpiriesMap = {};
          this.balanceExpiriesMap = result.reduce((map, expiry) => {
            map[expiry.timeOffBalanceId] = expiry.expiries;
            return map;
          }, balanceMap);
        },
      });
  }

  private getBalances() {
    this.loading = true;
    void combineLatest([
      this.employeeService.getBalances(this.employee.id, this.year()),
      this.store.select(getEmployeeEntities),
    ])
      .pipe(take(1))
      .subscribe({
        next: ([balances, employees]) => {
          // Set employee name as createdBy for corrections
          this.balances = balances.map((balance) => {
            balance.corrected.corrections = balance.corrected.corrections.map((correction) => {
              const employee = employees[correction.createdBy];
              return {
                ...correction,
                createdBy: employee ? employee.name : correction.createdBy,
              };
            });

            return balance;
          });

          this.updateBalanceStatusMap();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }
}
